import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { useWebSocket } from "../hooks/useWebSocket";
import type { WebSocketMessage } from "../context/WebSocketContext";
import { useToast, useTranslation } from "@repo/core";

/**
 * TEMPORARY: the backend always sends WebSocket "MESSAGE" text in English.
 * Until it sends translation keys instead of prose, map the known raw
 * strings to i18n keys here so they can be localized client-side. Remove
 * this once the backend is updated.
 */
const BACKEND_MESSAGE_TRANSLATION_KEYS: Record<string, string> = {
  "The backtest has been completed successfully.":
    "websocket.backendMessages.backtestCompleted",
};

/**
 * Component that handles WebSocket message processing and displays notifications via Toast
 * This component should be placed inside the WebSocket and Toast providers
 */
export const WebSocketMessageHandler = () => {
  const {
    subscribeToMessages,
    pendingReasoningRequests,
    removePendingReasoningRequest,
  } = useWebSocket();
  const queryClient = useQueryClient();
  const { t, tStrategy } = useTranslation();
  const {
    addInfoMessage,
    addWarningMessage,
    addErrorMessage,
    addSuccessMessage,
  } = useToast();

  // Track processed message UUIDs to prevent duplicate processing
  const processedMessagesRef = useRef<Set<string>>(new Set());

  // Cleanup processed messages periodically to prevent memory leaks
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const currentSize = processedMessagesRef.current.size;
      if (currentSize > 1000) {
        // Clear old messages if we have too many
        processedMessagesRef.current.clear();
        console.log(
          "📨 Cleared processed messages cache (was:",
          currentSize,
          "messages)"
        );
      }
    }, 5 * 60 * 1000); // Clean up every 5 minutes

    return () => clearInterval(cleanupInterval);
  }, []);

  /**
   * Force the matching queries to hit the network again.
   *
   * A plain invalidateQueries is not enough here. After a recalculation the
   * strategy queries refetch immediately, 404 while the backtest is still
   * running, and then sit in retry backoff (retry: 3, up to ~7s). If the
   * PREPARED broadcast lands inside that window, Query.fetch() sees
   * fetchStatus !== "idle" and - when the query has no data yet - only calls
   * retryer.continueRetry() and returns the in-flight promise. The invalidation
   * is swallowed, the retry chain runs out against the still-missing object,
   * and the chart never reloads.
   *
   * Cancelling first drops that retryer. The cancel has to be awaited: it only
   * rejects the retryer's promise, and the state transition back to "idle"
   * happens in the resulting microtask.
   */
  const reloadQueries = useCallback(
    async (queryKey: QueryKey) => {
      try {
        await queryClient.cancelQueries({ queryKey, type: "active" });
        // Mark unmounted matches stale too, so they refetch when remounted
        // instead of serving pre-backtest data from the 5 minute staleTime.
        await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
        await queryClient.refetchQueries({ queryKey, type: "active" });
      } catch (error) {
        console.error("📨 Failed to reload queries for key:", queryKey, error);
      }
    },
    [queryClient]
  );

  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      // Check if message has UUID and if it's already been processed
      if (message.uuid && processedMessagesRef.current.has(message.uuid)) {
        console.log(
          "📨 Ignoring duplicate WebSocket message with UUID:",
          message.uuid
        );
        return;
      }

      // Add UUID to processed messages set if it exists
      if (message.uuid) {
        processedMessagesRef.current.add(message.uuid);
      }

      switch (message.type) {
        case "MESSAGE": {
          // Display toast message using title, severity and message
          const { title, severity, message: text } = message;

          // Translate known backend strings; falls back to the raw English
          // text for anything not in the map (see comment on the map above).
          const localizedText =
            text && BACKEND_MESSAGE_TRANSLATION_KEYS[text]
              ? t(BACKEND_MESSAGE_TRANSLATION_KEYS[text], text)
              : text;

          switch (severity) {
            case "info":
              addInfoMessage(localizedText || "Info message received", title);
              break;
            case "warning":
              addWarningMessage(
                localizedText || "Warning message received",
                title
              );
              break;
            case "error":
              addErrorMessage(localizedText || "Error message received", title);
              break;
            default:
              addSuccessMessage(
                localizedText || "Success message received",
                title
              );
              break;
          }
          console.log("📨 Processing WebSocket MESSAGE message:", message);
          break;
        }

        // REFRESH and PREPARED both mean "the data behind this target changed".
        // They are handled identically: the target maps 1:1 onto a query key.
        case "REFRESH":
        case "PREPARED": {
          if (message.target) {
            const queryKey = message.target.split("/");
            console.log("📨 Reloading query with key:", queryKey);
            void reloadQueries(queryKey);
          }
          console.log(
            `📨 Processing WebSocket ${message.type} message:`,
            message
          );
          break;
        }

        case "dataPrepared": {
          // target format: "reasoning/{userId}#{strategyId}#{period}#{modelId}/{date}" (template)
          //            or "reasoning/{userId}#{modelId}/{date}" (user portfolio)
          // Query key format matches usePortfolioReasoning: ["reasoning", `${userId}_${strategyId}_${period}_${modelId}`, date]
          const target = message.target;
          if (target) {
            const [namespace, composite, date] = target.split("/");
            const segments = composite ? composite.split("#") : [];

            if (namespace === "reasoning" && segments.length === 4 && date) {
              const [userId, strategyId, period, modelId] = segments;
              const compositePK = `${userId}_${strategyId}_${period}_${modelId}`;
              const queryKey = ["reasoning", compositePK, date];
              const pendingKey = `${userId}#${strategyId}#${period}#${modelId}#${date}`;

              console.log("📨 Reloading reasoning query with key:", queryKey);
              void reloadQueries(queryKey);

              // Only toast/track cleanup for reasoning this session actually requested;
              // the cache above still refreshes in the background for anyone viewing it
              if (pendingReasoningRequests.has(pendingKey)) {
                removePendingReasoningRequest(pendingKey);
                addSuccessMessage(tStrategy("reasoning.dataReady"));
              }
            } else if (namespace === "reasoning" && segments.length === 2 && date) {
              // User portfolio: "reasoning/{userId}#{modelId}/{date}", sent to the owner only.
              // Query key matches useUserPortfolioReasoning: ["reasoning", modelId, date]
              const modelId = segments[1];
              const pendingKey = `${modelId}#${date}`;

              void reloadQueries(["reasoning", modelId, date]);

              if (pendingReasoningRequests.has(pendingKey)) {
                removePendingReasoningRequest(pendingKey);
                addSuccessMessage(tStrategy("reasoning.dataReady"));
              }
            }
          }
          console.log("📨 Processing WebSocket dataPrepared message:", message);
          break;
        }

        default:
          break;
      }
    },
    [
      reloadQueries,
      addInfoMessage,
      addWarningMessage,
      addErrorMessage,
      addSuccessMessage,
      pendingReasoningRequests,
      removePendingReasoningRequest,
      tStrategy,
      t,
    ]
  );

  // Keep the subscription itself stable so a re-render never misses a frame
  // while the listener is being swapped out.
  const handleMessageRef = useRef(handleMessage);
  handleMessageRef.current = handleMessage;

  useEffect(
    () => subscribeToMessages((message) => handleMessageRef.current(message)),
    [subscribeToMessages]
  );

  // This component doesn't render anything visible
  return null;
};

export default WebSocketMessageHandler;
